import { useState, useEffect, useCallback } from 'react';
import UpdateFournisseurForm from "../../components/update/update-fournisseur-form"; // Adjust path as needed based on your project structure
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading'; 

export default function Page() {
  const { token } = useAuth();
  const { id } = useParams(); 
  const [fournisseur, setFournisseur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFournisseur = useCallback(async () => {
    if (!id) {
      setError("L'ID du fournisseur est manquant."); 
      setLoading(false);
      return;
    }

    setLoading(true); // Start loading
    setError(null);    // Clear any previous errors
    try {
      if (!token) {
        toast.error("Vous n'êtes pas authentifié. Veuillez vous connecter."); // French: You are not authenticated. Please log in.
        setError("Jeton d'authentification introuvable. Veuillez vous connecter."); // French: Authentication token not found. Please log in.
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/fournisseurs/${id}`, { // Fetch specific fournisseur by ID
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`, // Include the authorization token
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `Échec de la récupération du fournisseur : ${res.statusText}`; // French: Failed to fetch supplier
        throw new Error(errorMessage);
      }

      const data = await res.json();
      // Assuming your API returns data in a { status: true, data: { ...fournisseur_object } } format
      if (data.status && data.data) {
        setFournisseur(data.data); // Set the fetched supplier data
      } else {
        throw new Error(data.message || "Format de données invalide de l'API."); // French: Invalid data format from API.
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du fournisseur :", err); // French: Error fetching supplier
      setError(err.message || "Une erreur inattendue est survenue lors de la récupération du fournisseur."); // French: An unexpected error occurred while fetching the supplier.
      toast.error(`Erreur : ${err.message || "Échec du chargement du fournisseur."}`); // French: Error: Failed to load supplier.
    } finally {
      setLoading(false); // End loading regardless of success or failure
    }
  }, [id]); // Dependency on 'id' ensures re-fetch if ID changes

  // useEffect to call the fetch function when the component mounts or fetchFournisseur changes
  useEffect(() => {
    fetchFournisseur();
  }, [fetchFournisseur]);

  // Conditional rendering based on loading, error, and data presence
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loading /> {/* Display loading spinner */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-2xl font-bold text-red-700 mb-4">Erreur lors du chargement du fournisseur</h2> {/* French: Error Loading Supplier */}
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={fetchFournisseur} // Allow retrying the fetch
            className="px-6 py-3 bg-[var(--color-primary-500)] text-white font-semibold rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
          >
            Réessayer {/* French: Retry */}
          </button>
        </div>
      </div>
    );
  }

  if (!fournisseur) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-700 mb-4">Fournisseur Introuvable</h2> {/* French: Supplier Not Found */}
          <p className="text-gray-600 text-xs">Le fournisseur avec l'ID "{id}" n'a pas pu être chargé ou n'existe pas.</p> {/* French: The supplier with ID "{id}" could not be loaded or does not exist. */}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl max-xl:text-3xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
            Modifier le Fournisseur
          </h1>
          <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl mx-auto">
            Modifiez les détails de ce fournisseur spécifique.
          </p>
        </div>

        {/* Form Container */}
        <div className="max-w-2xl mx-auto">
          {/* Pass the fetched fournisseur data to the UpdateFournisseurForm */}
          <UpdateFournisseurForm fournisseur={fournisseur} />
        </div>
      </div>
    </div>
  );
}
